import { useState } from "react"
import { useHistory } from "react-router-dom"
import axios from "axios"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import { Formik } from "formik"
import * as Yup from "yup"
import RichTextEditor from "../../../components/admin/common/RichTextEditor"
import FileUpload from "../../../components/admin/common/FileUpload"
import SpinnerLoader from "../../../components/common/SpinnerLoader"
import Header from "../../../components/admin/common/Header"

const CreateLegalDocument = () => {
  const [loading, setLoading] = useState(false)
  const [editorContent, setEditorContent] = useState("")
  const [uploadedFile, setUploadedFile] = useState(null)

  const history = useHistory()
  const authInfo = useSelector((state) => state.auth.authInfo)
  const userInfo = useSelector((state) => state.auth.userInfo)

  const categoryOptions = [
    { value: "terms_conditions", label: "Terms & Conditions" },
    { value: "privacy_policy", label: "Privacy Policy" },
    { value: "disclaimer", label: "Disclaimer" },
    { value: "ip_management", label: "IP Management" },
    { value: "cookie_policy", label: "Cookie Policy" },
    { value: "refund_policy", label: "Refund Policy" },
    { value: "community_guidelines", label: "Community Guidelines" },
    { value: "seller_agreement", label: "Seller Agreement" },
    { value: "buyer_agreement", label: "Buyer Agreement" },
    { value: "api_terms", label: "API Terms" },
  ]

  const typeOptions = [
    { value: "rich_text", label: "Rich Text Editor" },
    { value: "markdown", label: "Markdown" },
    { value: "html", label: "HTML" },
    { value: "static", label: "Static Text" },
    { value: "custom_upload", label: "File Upload" },
  ]

  const ipCategoryOptions = [
    { value: "stripe_related", label: "Stripe Related" },
    { value: "social_media", label: "Social Media" },
    { value: "signup_related", label: "Signup Related" },
    { value: "platform_assets", label: "Platform Assets" },
    { value: "logo_usage", label: "Logo Usage" },
    { value: "code_usage", label: "Code Usage" },
    { value: "trademark", label: "Trademark" },
    { value: "copyright", label: "Copyright" },
    { value: "data_protection", label: "Data Protection" },
    { value: "third_party_integrations", label: "Third Party Integrations" },
  ]

  const visibilityScopeOptions = [
    { value: "public", label: "Public" },
    { value: "buyer", label: "Buyers Only" },
    { value: "seller", label: "Sellers Only" },
    { value: "admin", label: "Admin Only" },
    { value: "law_body", label: "Law Body Only" },
  ]

  const agreementTriggerOptions = [
    { value: "signup", label: "User Signup" },
    { value: "service_creation", label: "Service Creation" },
    { value: "order_placement", label: "Order Placement" },
    { value: "order_acceptance", label: "Order Acceptance" },
    { value: "payment_processing", label: "Payment Processing" },
    { value: "profile_completion", label: "Profile Completion" },
    { value: "seller_verification", label: "Seller Verification" },
    { value: "api_access", label: "API Access" },
  ]

  const validationSchema = Yup.object({
    title: Yup.string()
      .min(5, "Title must be at least 5 characters")
      .max(200, "Title must not exceed 200 characters")
      .required("Title is required"),
    category: Yup.string().required("Category is required"),
    type: Yup.string().required("Document type is required"),
    visibilityScope: Yup.array().min(1, "At least one visibility scope is required"),
    ipCategory: Yup.string().when("category", {
      is: "ip_management",
      then: Yup.string().required("IP category is required for IP management documents"),
    }),
    effectiveFrom: Yup.date().required("Effective date is required"),
    seoTags: Yup.object({
      metaTitle: Yup.string().max(60, "Meta title must not exceed 60 characters"),
      metaDescription: Yup.string().max(160, "Meta description must not exceed 160 characters"),
    }),
  })

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true)

      const formData = new FormData()

      // Add all form fields
      Object.keys(values).forEach((key) => {
        if (key === "visibilityScope" || key === "agreementTriggers") {
          values[key].forEach((item) => {
            formData.append(`${key}[]`, item)
          })
        } else if (key === "seoTags") {
          Object.keys(values[key]).forEach((seoKey) => {
            if (values[key][seoKey]) {
              formData.append(`seoTags.${seoKey}`, values[key][seoKey])
            }
          })
        } else if (key === "complianceInfo") {
          Object.keys(values[key]).forEach((compKey) => {
            if (values[key][compKey]) {
              formData.append(`complianceInfo.${compKey}`, values[key][compKey])
            }
          })
        } else if (values[key] !== undefined && values[key] !== "") {
          formData.append(key, values[key])
        }
      })

      // Add content based on type
      if (values.type === "custom_upload" && uploadedFile) {
        formData.append("file", uploadedFile)
      } else {
        formData.append("content", editorContent)
      }

      const response = await axios.post("admin/legaldocument/documents", formData, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
          "Content-Type": "multipart/form-data",
        },
      })

      if (response.data.status) {
        toast.success("Legal document created successfully")
        history.push("/admin/legal-documents")
      }
    } catch (error) {
      console.error("Error creating document:", error)
      toast.error(error.response?.data?.message || "Failed to create document")
    } finally {
      setLoading(false)
      setSubmitting(false)
    }
  }

  return (
    <>
    <Header />
    <div 
      className="create-legal-document" 
      style={{ 
        minHeight: "100vh", 
        overflowY: "auto", 
        padding: '18px 100px' 
      }}
    >
      <style jsx>{`
        @media (max-width: 1200px) {
          .create-legal-document {
            padding: 18px 50px !important;
          }
        }
        
        @media (max-width: 992px) {
          .create-legal-document {
            padding: 18px 30px !important;
          }
          
          .form-sidebar {
            margin-top: 2rem;
          }
        }
        
        @media (max-width: 768px) {
          .create-legal-document {
            padding: 15px 20px !important;
          }
          
          .page-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 15px;
          }
          
          .page-header .btn {
            width: 100%;
            justify-content: center;
          }
          
          .page-header h2 {
            font-size: 1.5rem;
          }
          
          .page-header p {
            font-size: 0.875rem;
          }
          
          .form-sidebar {
            margin-top: 1.5rem;
          }
          
          .card-header h5 {
            font-size: 1rem;
          }
          
          .form-check-group {
            max-height: 200px;
            overflow-y: auto;
          }
          
          .submit-button {
            position: sticky;
            bottom: 0;
            background: white;
            padding: 1rem 0;
            margin: 0 -1rem;
            border-top: 1px solid #dee2e6;
          }
        }
        
        @media (max-width: 576px) {
          .create-legal-document {
            padding: 12px 15px !important;
          }
          
          .page-header h2 {
            font-size: 1.25rem;
          }
          
          .page-header p {
            font-size: 0.8rem;
          }
          
          .card {
            margin-bottom: 1rem;
          }
          
          .card-body {
            padding: 1rem;
          }
          
          .card-header {
            padding: 0.75rem 1rem;
          }
          
          .form-label {
            font-size: 0.875rem;
            font-weight: 600;
          }
          
          .form-control,
          .form-select {
            font-size: 0.875rem;
            padding: 0.5rem 0.75rem;
          }
          
          .form-check-label {
            font-size: 0.875rem;
          }
          
          .btn {
            font-size: 0.875rem;
            padding: 0.5rem 1rem;
          }
          
          .rich-text-editor {
            min-height: 250px !important;
          }
          
          .submit-button .btn {
            font-size: 1rem;
            padding: 0.75rem 1.5rem;
          }
        }
        
        @media (max-width: 480px) {
          .create-legal-document {
            padding: 10px 12px !important;
          }
          
          .card-body {
            padding: 0.75rem;
          }
          
          .card-header {
            padding: 0.5rem 0.75rem;
          }
          
          .form-control,
          .form-select {
            font-size: 0.8rem;
            padding: 0.375rem 0.5rem;
          }
          
          .rich-text-editor {
            min-height: 200px !important;
          }
        }
        
        /* Checkbox groups responsive styling */
        .checkbox-group {
          max-height: none;
        }
        
        @media (max-width: 768px) {
          .checkbox-group {
            max-height: 150px;
            overflow-y: auto;
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            padding: 0.5rem;
            background-color: #f8f9fa;
          }
        }
        
        /* Rich text editor responsive */
        .editor-container {
          max-height: 400px;
          overflow-y: auto;
        }
        
        @media (max-width: 768px) {
          .editor-container {
            max-height: 300px;
          }
        }
        
        @media (max-width: 576px) {
          .editor-container {
            max-height: 250px;
          }
        }
      `}</style>
      
      {loading && <SpinnerLoader />}

      <div className="container-fluid" style={{ paddingBottom: "2rem" }}>
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-4 page-header">
              <div>
                <h2 className="mb-1">Create Legal Document</h2>
                <p className="text-muted">Create new legal content, terms, or IP agreements</p>
              </div>
              <button onClick={() => history.goBack()} className="btn btn-outline-secondary">
                Back
              </button>
            </div>

            <Formik
              initialValues={{
                title: "",
                category: "",
                type: "rich_text",
                visibilityScope: ["public"],
                ipCategory: "",
                requiresAgreement: false,
                agreementTriggers: [],
                effectiveFrom: new Date().toISOString().split("T")[0],
                expiresAt: "",
                adminNotes: "",
                seoTags: {
                  metaTitle: "",
                  metaDescription: "",
                  keywords: "",
                  canonicalUrl: "",
                },
                complianceInfo: {
                  jurisdiction: "",
                  lawReference: "",
                  nextReviewDate: "",
                },
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-lg-8">
                      <div className="card">
                        <div className="card-header">
                          <h5 className="mb-0">Document Details</h5>
                        </div>
                        <div className="card-body">
                          {/* Title */}
                          <div className="mb-3">
                            <label className="form-label">
                              Document Title <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${touched.title && errors.title ? "is-invalid" : ""}`}
                              name="title"
                              value={values.title}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Enter document title"
                            />
                            {touched.title && errors.title && <div className="invalid-feedback">{errors.title}</div>}
                          </div>

                          {/* Category and Type */}
                          <div className="row">
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Category <span className="text-danger">*</span>
                                </label>
                                <select
                                  className={`form-select ${touched.category && errors.category ? "is-invalid" : ""}`}
                                  name="category"
                                  value={values.category}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                >
                                  <option value="">Select Category</option>
                                  {categoryOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                {touched.category && errors.category && (
                                  <div className="invalid-feedback">{errors.category}</div>
                                )}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Document Type <span className="text-danger">*</span>
                                </label>
                                <select
                                  className={`form-select ${touched.type && errors.type ? "is-invalid" : ""}`}
                                  name="type"
                                  value={values.type}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                >
                                  {typeOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                {touched.type && errors.type && <div className="invalid-feedback">{errors.type}</div>}
                              </div>
                            </div>
                          </div>

                          {/* IP Category (conditional) */}
                          {values.category === "ip_management" && (
                            <div className="mb-3">
                              <label className="form-label">
                                IP Category <span className="text-danger">*</span>
                              </label>
                              <select
                                className={`form-select ${touched.ipCategory && errors.ipCategory ? "is-invalid" : ""}`}
                                name="ipCategory"
                                value={values.ipCategory}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="">Select IP Category</option>
                                {ipCategoryOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              {touched.ipCategory && errors.ipCategory && (
                                <div className="invalid-feedback">{errors.ipCategory}</div>
                              )}
                            </div>
                          )}

                          {/* Content Editor */}
                          <div className="mb-3">
                            <label className="form-label">
                              Content <span className="text-danger">*</span>
                            </label>
                            <div className="editor-container">
                              {values.type === "custom_upload" ? (
                                <FileUpload
                                  onFileSelect={setUploadedFile}
                                  acceptedTypes=".pdf,.doc,.docx,.txt,.html,.md"
                                  maxSize={50 * 1024 * 1024} // 50MB
                                />
                              ) : (
                                <RichTextEditor
                                  value={editorContent}
                                  onChange={setEditorContent}
                                  placeholder="Enter document content..."
                                  type={values.type}
                                />
                              )}
                            </div>
                          </div>

                          {/* Admin Notes */}
                          <div className="mb-3">
                            <label className="form-label">Admin Notes</label>
                            <textarea
                              className="form-control"
                              name="adminNotes"
                              value={values.adminNotes}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              rows="3"
                              placeholder="Internal notes about this document..."
                            />
                          </div>
                        </div>
                      </div>

                      {/* SEO Settings */}
                      <div className="card mt-4">
                        <div className="card-header">
                          <h5 className="mb-0">SEO Settings</h5>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Meta Title</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="seoTags.metaTitle"
                                  value={values.seoTags.metaTitle}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  placeholder="SEO meta title"
                                  maxLength="60"
                                />
                                <small className="text-muted">{values.seoTags.metaTitle.length}/60 characters</small>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Canonical URL</label>
                                <input
                                  type="url"
                                  className="form-control"
                                  name="seoTags.canonicalUrl"
                                  value={values.seoTags.canonicalUrl}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  placeholder="https://example.com/legal/document"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Meta Description</label>
                            <textarea
                              className="form-control"
                              name="seoTags.metaDescription"
                              value={values.seoTags.metaDescription}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              rows="2"
                              placeholder="SEO meta description"
                              maxLength="160"
                            />
                            <small className="text-muted">{values.seoTags.metaDescription.length}/160 characters</small>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Keywords</label>
                            <input
                              type="text"
                              className="form-control"
                              name="seoTags.keywords"
                              value={values.seoTags.keywords}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="keyword1, keyword2, keyword3"
                            />
                            <small className="text-muted">Separate keywords with commas</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-4 form-sidebar">
                      {/* Publishing Settings */}
                      <div className="card">
                        <div className="card-header">
                          <h5 className="mb-0">Publishing Settings</h5>
                        </div>
                        <div className="card-body">
                          {/* Visibility Scope */}
                          <div className="mb-3">
                            <label className="form-label">
                              Visibility Scope <span className="text-danger">*</span>
                            </label>
                            <div className="checkbox-group">
                              {visibilityScopeOptions.map((option) => (
                                <div key={option.value} className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`visibility-${option.value}`}
                                    checked={values.visibilityScope.includes(option.value)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFieldValue("visibilityScope", [...values.visibilityScope, option.value])
                                      } else {
                                        setFieldValue(
                                          "visibilityScope",
                                          values.visibilityScope.filter((v) => v !== option.value),
                                        )
                                      }
                                    }}
                                  />
                                  <label className="form-check-label" htmlFor={`visibility-${option.value}`}>
                                    {option.label}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Effective Dates */}
                          <div className="mb-3">
                            <label className="form-label">
                              Effective From <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className={`form-control ${touched.effectiveFrom && errors.effectiveFrom ? "is-invalid" : ""}`}
                              name="effectiveFrom"
                              value={values.effectiveFrom}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.effectiveFrom && errors.effectiveFrom && (
                              <div className="invalid-feedback">{errors.effectiveFrom}</div>
                            )}
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Expires At</label>
                            <input
                              type="date"
                              className="form-control"
                              name="expiresAt"
                              value={values.expiresAt}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <small className="text-muted">Leave empty for no expiration</small>
                          </div>
                        </div>
                      </div>

                      {/* Agreement Settings */}
                      <div className="card mt-4">
                        <div className="card-header">
                          <h5 className="mb-0">Agreement Settings</h5>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="requiresAgreement"
                                name="requiresAgreement"
                                checked={values.requiresAgreement}
                                onChange={handleChange}
                              />
                              <label className="form-check-label" htmlFor="requiresAgreement">
                                Requires User Agreement
                              </label>
                            </div>
                          </div>

                          {values.requiresAgreement && (
                            <div className="mb-3">
                              <label className="form-label">Agreement Triggers</label>
                              <div className="checkbox-group">
                                {agreementTriggerOptions.map((option) => (
                                  <div key={option.value} className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`trigger-${option.value}`}
                                      checked={values.agreementTriggers.includes(option.value)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setFieldValue("agreementTriggers", [...values.agreementTriggers, option.value])
                                        } else {
                                          setFieldValue(
                                            "agreementTriggers",
                                            values.agreementTriggers.filter((t) => t !== option.value),
                                          )
                                        }
                                      }}
                                    />
                                    <label className="form-check-label" htmlFor={`trigger-${option.value}`}>
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Compliance Info */}
                      <div className="card mt-4">
                        <div className="card-header">
                          <h5 className="mb-0">Compliance Information</h5>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <label className="form-label">Jurisdiction</label>
                            <input
                              type="text"
                              className="form-control"
                              name="complianceInfo.jurisdiction"
                              value={values.complianceInfo.jurisdiction}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="e.g., US, EU, Global"
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Law Reference</label>
                            <input
                              type="text"
                              className="form-control"
                              name="complianceInfo.lawReference"
                              value={values.complianceInfo.lawReference}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="e.g., GDPR Article 13"
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Next Review Date</label>
                            <input
                              type="date"
                              className="form-control"
                              name="complianceInfo.nextReviewDate"
                              value={values.complianceInfo.nextReviewDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div className="card mt-4">
                        <div className="card-body submit-button">
                          <button type="submit" className="btn btn-primary w-100" disabled={isSubmitting || loading}>
                            {isSubmitting || loading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Creating...
                              </>
                            ) : (
                              <>
                                Create Document
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default CreateLegalDocument